// Centralized catalog of placeholder modules.
//
// Each entry declares the badge tier (beta / pro / enterprise) and copy that
// the PlaceholderModulePage uses when the route is opened.
//
// IMPORTANT: only modules that are NOT yet fully shipped should appear here.
// Once a module ships, remove the entry — the sidebar will route to the
// real page instead of the placeholder.

export const MODULE_TIERS = {
  beta: {
    id: "beta",
    label: "BETA",
    tone: "teal",
    eyebrow: "Funzionalità in versione anticipata",
    cta_label: "Richiedi accesso anticipato",
    cta_subtitle: "Disponibile in versione anticipata per i clienti selezionati.",
  },
  pro: {
    id: "pro",
    label: "PRO",
    tone: "navy",
    eyebrow: "Disponibile nei piani Pro",
    cta_label: "Aggiorna al piano Pro",
    cta_subtitle: "Sblocca questa funzionalità con il piano Pro o superiore.",
  },
  enterprise: {
    id: "enterprise",
    label: "ENTERPRISE",
    tone: "navy_solid",
    eyebrow: "Disponibile nel piano Enterprise",
    cta_label: "Parla con il team enterprise",
    cta_subtitle: "Funzionalità dedicata alle strutture multi-location e ai gruppi.",
  },
};

// moduleId → tier + descriptive copy.
// Keep titles in Italian (app i18n is Italian-first).
export const PLACEHOLDER_MODULES = {
  waste_control: {
    tier: "beta",
    title: "Controllo sprechi",
    summary:
      "Monitora ogni grammo che esce dalla cucina senza essere venduto. Categorizza gli sprechi, calcola il costo reale e crea report giornalieri automatici.",
    benefits: [
      "Tracciamento sprechi per categoria, reparto e operatore.",
      "Stima impatto economico in tempo reale (€/mese sprecati).",
      "Suggerimenti per ridurre lo spreco target ≤ 3% dei ricavi.",
    ],
  },
  margin_analysis: {
    tier: "beta",
    title: "Analisi marginalità",
    summary:
      "Capisci esattamente quali piatti e categorie ti fanno guadagnare e quali erodono il margine. Heat-map per categoria, top performer, sotto-margine.",
    benefits: [
      "Margine € e % per ogni piatto, categoria e fascia oraria.",
      "Confronto food cost teorico vs. effettivo.",
      "Alert automatici quando un piatto scende sotto la soglia di marginalità.",
    ],
  },
  break_even: {
    tier: "beta",
    title: "Punto di pareggio",
    summary:
      "Calcola il break-even della struttura su base mensile: quanti coperti devi fare per coprire costi fissi + variabili, e quanto manca al target.",
    benefits: [
      "Break-even dinamico per giornata, settimana e mese.",
      "Gap di coperti rispetto al pareggio in tempo reale.",
      "Simulazione del pareggio con scenario prezzi + food cost.",
    ],
  },
  business_analytics: {
    tier: "enterprise",
    title: "Business Analytics",
    summary:
      "Una piattaforma di analisi avanzata pensata per gruppi multi-location: cohort analysis, customer lifetime value, predictive forecasting e benchmark di settore.",
    benefits: [
      "Modelli predittivi su ricavi, food cost e occupancy.",
      "Cohort behavioral analysis sui clienti diretti e OTA.",
      "Esportazioni SQL/BI integrazione Looker / Power BI / Tableau.",
    ],
  },
  reports: {
    tier: "pro",
    title: "Report avanzati",
    summary:
      "Genera report PDF/Excel personalizzati per direzione, commercialista e investitori. Sezioni configurabili, branding tenant, invio programmato via email.",
    benefits: [
      "Report fiscali, operativi e di marginalità con un click.",
      "Pianificazione automatica (giornaliera, settimanale, mensile).",
      "Distribuzione via email con allegati firmati e watermark.",
    ],
  },
  notifications: {
    tier: "pro",
    title: "Centro notifiche",
    summary:
      "Tutte le notifiche operative aggregate in un unico hub: alert AI, ordini critici, scadenze, comunicazioni cliente. Push web, email, Slack e Telegram.",
    benefits: [
      "Regole personalizzate per ruolo (owner, manager, staff).",
      "Routing multi-canale: in-app, email, Slack, Telegram.",
      "Snooze, escalation automatica, audit completo.",
    ],
  },
  // Generic fallbacks for other modules in MODULE_NAV_GROUPS still in roadmap.
  ai_forecast: {
    tier: "beta",
    title: "AI Forecast",
    summary:
      "Previsione domanda e ricavi con modelli AI. Consiglia quanto produrre, ordinare e assumere settimana per settimana.",
    benefits: [
      "Previsione ricavi 7/14/30 giorni con confidenza.",
      "Suggerimenti su ordini freschi e turni.",
      "Apprende dai tuoi dati storici tenant.",
    ],
  },
  breakfast_management: {
    tier: "beta",
    title: "Gestione colazioni hotel",
    summary:
      "Modulo dedicato alla gestione completa del servizio colazione: copertura ospiti, costo per coperto, sprechi, soddisfazione e marginalità.",
    benefits: [
      "Forecast coperti basato su check-in e fascia tariffaria.",
      "Tracciamento sprechi buffet con cost analysis.",
      "Reportistica giornaliera e mensile alla direzione.",
    ],
  },
  revenue_management: {
    tier: "enterprise",
    title: "Revenue Management",
    summary:
      "Strategia tariffaria dinamica per la struttura: pricing per giorno, evento, segmento; analisi cancellazioni e gestione overbooking controllato.",
    benefits: [
      "Pricing dinamico multi-segmento con regole personalizzabili.",
      "Analisi cancellazioni e politiche di rimborso.",
      "Channel manager con sincronizzazione OTA in tempo reale.",
    ],
  },
  crm_marketing: {
    tier: "pro",
    title: "CRM & Marketing",
    summary:
      "Costruisci la tua audience diretta: anagrafica clienti, segmentazione, campagne email/SMS, programma fedeltà e analisi LTV.",
    benefits: [
      "Segmentazione clienti diretti + OTA con dati centralizzati.",
      "Campagne email/SMS con template e A/B test.",
      "Programma fedeltà punti + tier + premi configurabili.",
    ],
  },
};

export function getPlaceholderModule(moduleId) {
  return PLACEHOLDER_MODULES[moduleId] || null;
}

export function getModuleTier(moduleId) {
  const m = PLACEHOLDER_MODULES[moduleId];
  if (!m) return null;
  return MODULE_TIERS[m.tier] || null;
}
